import { Star } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { paletteFaceIcon } from "@/shared/components/SentimentBadge";
import { usePolarityChoices } from "@/shared/components/polarity-choices";
import { polarityLabel, polarityOptions, polarityPalette } from "@/shared/types/polarity";
import type { Mention } from "@/shared/types/mention";

// Do menor nível pro maior (1 → 3 estrelas), a mesma direção das reações:
// do pior pro melhor.
const APPROACHES = [
  { value: "citation", label: "Citação", stars: 1 },
  { value: "supporting", label: "Coadjuvante", stars: 2 },
  { value: "protagonist", label: "Protagonista", stars: 3 }
] as const;

/**
 * Polaridade e abordagem editáveis no próprio card da lista (2026-10-13),
 * sem passar pelo editor — que só abria pelo título, e nem todo post tem
 * título. Desde 2026-10-15 são botões lado a lado, sem menu: ícones de
 * rosto pra polaridade e três estrelas pra abordagem, os dois do pior pro
 * melhor e cada um com tooltip. Grava pela mesma rota do editor e do Modo
 * revisão (`updateMentionClassification`), com o valor novo na tela na
 * hora e de volta ao antigo se a gravação falhar.
 *
 * Em IMPRENSA com citação apurada ganha um terceiro segmento, "Citação
 * no título" (2026-10-22): o título cita a marca? Um botão só com a
 * leitura atual; clicar alterna Sim↔Não. `null` quer dizer "não se
 * aplica" (rede, TV/rádio, imprensa sem nota): o segmento não existe,
 * porque o servidor recusaria o PATCH.
 */
export function MentionQuickClassify({
  mention,
  token,
  onSaved,
  onError,
  readOnly = false
}: {
  mention: Mention;
  token: string;
  onSaved: () => void;
  onError: (message: string) => void;
  /** Quem só vê (o Visualizador, 2026-10-22): os botões mostram, não gravam. */
  readOnly?: boolean;
}) {
  const polarities = usePolarityChoices();
  const [pending, setPending] = useState<{
    polarity?: string;
    approach?: string;
    titleCitation?: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  // Menção descartada é travada: só restaurar (decisão da dona, 2026-10-18).
  const locked = mention.discardedAt !== null || readOnly;
  const lockedTitle = readOnly
    ? "Somente leitura"
    : locked
      ? "Restaure a menção pra editar"
      : undefined;

  // Dado novo do servidor: o otimista sai.
  useEffect(() => {
    setPending(null);
  }, [mention.polarity, mention.approachValue, mention.titleCitation]);

  const polarity = pending?.polarity ?? mention.polarity;
  const approach = pending?.approach ?? mention.approachValue;
  const titleCitation = pending?.titleCitation ?? mention.titleCitation;
  const titleCitationText = titleCitation ? "Sim" : "Não";
  const stars = APPROACHES.find(item => item.value === approach)?.stars ?? 0;
  const approachText = APPROACHES.find(item => item.value === approach)?.label;
  // As polaridades que a escala da empresa oferece. Menção com uma polaridade
  // fora da escala (FAVORAVEL numa empresa que virou 3 níveis) continua
  // aparecendo marcada, como o selo antigo mostrava qualquer valor.
  const base =
    polarity && !polarities.some(choice => choice.name === polarity)
      ? [...polarities, { name: polarity, label: polarityLabel(polarity), palette: polarityPalette(polarity) }]
      : polarities;
  // Do pior pro melhor. O hook entrega do melhor pro pior, então inverte; com
  // o domínio carregado, reordena pela posição dele (é o que põe uma
  // polaridade fora da escala no lugar certo e não no fim).
  const rank = (name: string) => {
    const index = polarityOptions().findIndex(option => option.name === name);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  const reactions = [...base].reverse().sort((a, b) => rank(b.name) - rank(a.name));
  const [hoverStars, setHoverStars] = useState<number | null>(null);
  const shownStars = hoverStars ?? stars;

  const save = async (change: {
    polarity?: string;
    approach?: string;
    titleCitation?: boolean;
  }) => {
    setPending(current => ({ ...current, ...change }));
    setBusy(true);
    try {
      await apiClient.updateMentionClassification(token, mention.id, change);
      onSaved();
    } catch (failure) {
      setPending(null);
      onError(errorMessage(failure, "Não foi possível salvar a classificação."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="quick-field">
        <span className="quick-field-label">Polaridade</span>
        <div className="quick-react" role="group" aria-label={`Polaridade: ${polarityLabel(polarity)}`}>
          {reactions.map(reaction => {
            const selected = reaction.name === polarity;
            const Icon = paletteFaceIcon[reaction.palette];
            return (
              <button
                key={reaction.name}
                type="button"
                className={`quick-react-btn mention-data-tooltip ${reaction.palette}${selected ? " is-selected" : ""}`}
                aria-pressed={selected}
                aria-label={reaction.label}
                data-tooltip={reaction.label}
                disabled={busy || locked}
                title={lockedTitle}
                onClick={() => {
                  if (!selected) void save({ polarity: reaction.name });
                }}
              >
                <Icon size={18} aria-hidden="true"/>
              </button>
            );
          })}
        </div>
      </div>
      <div className="quick-field">
        <span className="quick-field-label">Abordagem</span>
        <div
          className="quick-stars"
          role="group"
          aria-label={`Abordagem: ${approachText ?? "não classificada"}`}
          onMouseLeave={() => setHoverStars(null)}
        >
          {APPROACHES.map(item => (
            <button
              key={item.value}
              type="button"
              className={`quick-star-btn mention-data-tooltip${item.stars <= shownStars ? " is-filled" : ""}`}
              aria-pressed={item.value === approach}
              aria-label={item.label}
              data-tooltip={item.label}
              data-tooltip-position="bottom"
              disabled={busy || locked}
              title={lockedTitle}
              onMouseEnter={() => !locked && setHoverStars(item.stars)}
              onFocus={() => !locked && setHoverStars(item.stars)}
              onBlur={() => setHoverStars(null)}
              onClick={() => {
                if (item.value !== approach) void save({ approach: item.value });
              }}
            >
              <Star size={18} aria-hidden="true"/>
            </button>
          ))}
        </div>
      </div>
      {mention.mediaType === "press" && titleCitation !== null && (
        <div className="quick-field">
          <span className="quick-field-label">Citação no título</span>
          <button
            type="button"
            className={`quick-title-toggle mention-data-tooltip${titleCitation ? " is-on" : ""}`}
            aria-pressed={titleCitation}
            aria-label={`Citação no título: ${titleCitationText}`}
            data-tooltip={titleCitation ? "Marcar como Não" : "Marcar como Sim"}
            data-tooltip-position="bottom"
            disabled={busy || locked}
            title={lockedTitle}
            onClick={() => void save({ titleCitation: !titleCitation })}
          >
            {titleCitationText}
          </button>
        </div>
      )}
    </>
  );
}
